// react
import { useCallback, useMemo } from 'react';

// store
import { useListAllOrganiseEntriesQuery } from 'store/api/organise';

// types
import {
  OrganiseCareCircleEntries,
  OrganiseEntryStatus,
  OrganiseMember,
} from '@karehero/models';
import { formatRecurrence } from '@karehero/helpers/datetime';
import { OrganiserCareCircle } from '@karehero/llama/pwa/pages/OrganiserPage/OrganiserPage';
import { OrganiserEntryItem } from '@karehero/llama/pwa/organisms/OrganiserEntryRow/OrganiserEntryRow';
import { useOrganiserActionsWithTracking } from './useTrackedOrganiserActions';

const sortUndefinedBeforeAscending = (
  a: OrganiserEntryItem,
  b: OrganiserEntryItem,
) => {
  const aHasDate = !!a.startTime;
  const bHasDate = !!b.startTime;
  if (!aHasDate && bHasDate) return -1;
  if (aHasDate && !bHasDate) return 1;
  const aTime = a.startTime ?? '';
  const bTime = b.startTime ?? '';
  return aTime.localeCompare(bTime);
};

const buildMemberMap = (
  data: OrganiseCareCircleEntries[],
): Map<string, OrganiseMember> => {
  const map = new Map<string, OrganiseMember>();
  for (const group of data) {
    for (const m of group.members) {
      if (!map.has(m.id)) {
        map.set(m.id, m);
      }
    }
  }
  return map;
};

const mergeEntries = (
  data: OrganiseCareCircleEntries[],
  activeCareCircles: OrganiserCareCircle[],
  memberMap: Map<string, OrganiseMember>,
): OrganiserEntryItem[] => {
  const items: OrganiserEntryItem[] = [];

  data.forEach((group) => {
    const activeCareCircle = activeCareCircles.find(
      (cc) => cc.id === group.careCircleId,
    );

    if (!activeCareCircle) return;

    group.entries.forEach((entry) => {
      const assignee = entry.assigneeAccountId
        ? memberMap.get(entry.assigneeAccountId)
        : undefined;

      items.push({
        id: entry.id,
        calendarId: entry.calendarId,
        title: entry.title,
        status: entry.status as OrganiseEntryStatus,
        assigneeName: assignee?.name,
        assigneeProfile: assignee,
        createdByName: entry.createdByName,
        startTime: entry.startTime,
        endTime: entry.endTime,
        link: entry.link,
        careCircleColor: activeCareCircle.color,
        careCircleName: group.careCircleName,
        recurringEventId: entry.recurringEventId,
        recurrenceDescription: entry.recurrence
          ? formatRecurrence(entry.recurrence.freq, entry.recurrence.every)
          : undefined,
        occurrenceDate: entry.occurrenceDate,
      });
    });
  });

  items.sort(sortUndefinedBeforeAscending);

  return items;
};

export const useOrganiserActions = (
  activeCareCircles: OrganiserCareCircle[],
  options?: { skip?: boolean },
) => {
  const { changeStatus, ...actionsWithTracking } =
    useOrganiserActionsWithTracking();

  const { data: rawOrganiseEntries, isLoading } =
    useListAllOrganiseEntriesQuery(undefined, {
      skip: options?.skip,
    });

  const memberMap = useMemo(
    () => buildMemberMap(rawOrganiseEntries ?? []),
    [rawOrganiseEntries],
  );

  const entries: OrganiserEntryItem[] = useMemo(
    () => mergeEntries(rawOrganiseEntries ?? [], activeCareCircles, memberMap),
    [rawOrganiseEntries, activeCareCircles, memberMap],
  );

  const calendars = useMemo(
    () =>
      (rawOrganiseEntries ?? []).map((group) => ({
        calendarId: group.calendar.id,
        label: group.careCircleName,
        members: group.members,
      })),
    [rawOrganiseEntries],
  );

  const findRawEntry = useCallback(
    (entryId: string) => {
      for (const group of rawOrganiseEntries ?? []) {
        const entry = group.entries.find((e) => e.id === entryId);
        if (entry) return entry;
      }
      return undefined;
    },
    [rawOrganiseEntries],
  );

  const handleStatusChange = useCallback(
    async (entryId: string, status: OrganiseEntryStatus) => {
      const entry = findRawEntry(entryId);

      if (!entry) return;

      await changeStatus(entry, status);
    },
    [findRawEntry, changeStatus],
  );

  return {
    ...actionsWithTracking,
    isLoading,
    entries,
    calendars,
    findRawEntry,
    changeStatus: handleStatusChange,
  };
};
